import * as React from 'react';

import { useRouter } from 'next/router';

import { SingleSelectInputOption } from '@/components/display/select/single-select-dropdown';
import { withExceptionCatcher } from '@/components/hoc/with-exception-catcher';
import ExpandableGrantsProgramsPageLayout from '@/components/pages/grants/layout/expandable-grants-programs-page-layout';
import GrantsProgramsPageLayout from '@/components/pages/grants/layout/grants-programs-page-layout';
import GrantsPageLayout from '@/components/pages/grants/layout/individual-grants-page-layout';
import { IMetaProps } from '@/layout/head/default-meta';
import { GrantsEntities } from '@/types/pages/api/grants/grants-entities';
import uniq from '@/utils/functions/array/uniq';

export interface GrantsPageControllerProps extends GrantsEntities {
    meta: IMetaProps;
}

export const GrantsPageController = ({ projectsById, ecosystemsById, grantsById, meta }: GrantsPageControllerProps) => {
    const router = useRouter();

    const grants = React.useMemo(() => {
        return Object.values(projectsById).map(({ grants: grantIds, ...project }) => ({
            ...project,
            grants: grantIds.map(id => grantsById[id]!),
        }));
    }, [projectsById, grantsById]);

    const individualGrants = React.useMemo(() => {
        return Object.values(grantsById).map(({ project, ...grant }) => ({
            ...grant,
            project: projectsById[project!]!,
        }));
    }, [grantsById, projectsById]);

    const categories: SingleSelectInputOption[] = React.useMemo(
        () =>
            uniq(grants.map(g => g.categories).flat(), true).map(category => ({
                id: category,
                name: category,
            })),
        [grants],
    );

    const grantsTypes: SingleSelectInputOption[] = React.useMemo(
        () =>
            uniq(individualGrants.map(g => g.categories).flat(), true).map(category => ({
                id: category,
                name: category,
            })),
        [individualGrants],
    );

    if ('simple' in router.query) {
        return (
            <ExpandableGrantsProgramsPageLayout
                categories={categories}
                grants={grants}
                assets={{}}
                ecosystems={ecosystemsById}
                grantsTypes={grantsTypes}
                title={meta.title || ''}
                counts={[individualGrants.length, grants.length]}
            />
        );
    }

    return router.asPath === '/grants/programs' ? (
        <GrantsProgramsPageLayout
            categories={categories}
            grants={grants}
            ecosystems={ecosystemsById}
            grantsTypes={grantsTypes}
            title={meta.title || ''}
            counts={[individualGrants.length, grants.length]}
        />
    ) : (
        <GrantsPageLayout
            categories={categories}
            ecosystems={ecosystemsById}
            grantsTypes={grantsTypes}
            individualGrants={individualGrants}
            selectedProgram={router.query.program ? (router.query.program as string) : null}
            title={meta.title || ''}
            counts={[individualGrants.length, grants.length]}
        />
    );
};

export default withExceptionCatcher(GrantsPageController);
